import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a product SKU.
 * This component listens for variant update events and updates the SKU display accordingly.
 * It handles SKU updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} productVariantSkuValue - The element that displays only the SKU value (prefix stays outside this node)
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'productVariantSkuValue'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateSku);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
  }

  /**
   * Updates the SKU.
   * @param {Event} event - The variant update event.
   */
  updateSku = (event) => {
    const showSku = this.dataset.showSku !== 'false';
    const showVendor = this.dataset.showVendor === 'true';

    if (!showSku) {
      if (showVendor && this.refs.vendor?.textContent.trim()) {
        this.style.display = 'block';
      }
      return;
    }

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    // Use the variant data from the event
    // The variant is in event.detail.resource
    if (event.detail.resource) {
      const productVariantSku = event.detail.resource.sku || '';

      if (productVariantSku) {
        this.style.display = 'block';
        this.refs.productVariantSkuValue.textContent = productVariantSku;
      } else if (showVendor && this.refs.vendor?.textContent.trim()) {
        this.style.display = 'block';
        this.refs.productVariantSkuValue.textContent = '';
      } else {
        this.style.display = 'none';
        this.refs.productVariantSkuValue.textContent = '';
      }
    }
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}
